import { faBrowser, faBuildings, faCog } from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faCrystalBall, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Popover, PopoverContent, PopoverTrigger, sourTwMerge, TextField, Tooltip } from '@plaidev/sour-react';
import { type Dispatch, type ReactNode, type SetStateAction, useState } from 'react';
import type { GlobalNaviOrganizationV2, Platform } from '../contract/index.js';
import { AUTH_ROUTE_TEMPLATES, fillAuthRouteTemplate, resolveAuthHref } from '../internal/authRoutes.js';
import { resolveProjectSettingsHref } from '../internal/settingsHref.js';
import type { HrefBuilder } from '../internal/urls.js';

export type ProjectSwitcherPopoverProps = {
  organizations: GlobalNaviOrganizationV2[];
  /**
   * 現在プロジェクトの行キー（`qant:<id>` / `karte:<id>`）。
   * current 未確定時は undefined（どの行も「現在」にしない）。
   */
  currentProjectKey?: string;
  selfPlatform: Platform;
  buildHref: HrefBuilder;
  trigger: ReactNode;
  onOpenChange?: (open: boolean) => void;
  /**
   * QANT ホスト向け。`qantProjectId` / `qantOrganizationId` がある行だけ呼ばれる。
   * KARTE-only 行は package が別タブ遷移するため呼ばない。
   */
  onProjectSwitch: (params: { projectId: string; organizationId: string }) => void;
  /**
   * プロジェクト行 (<a href>) の遷移先を解決する。指定すると各行が実リンクになり、
   * ⌘/Ctrl・中クリックでの別タブ遷移がブラウザ標準で効くようになる。
   * 未指定時は従来通り onProjectSwitch のみで遷移する。
   */
  resolveProjectHref?: (params: { organizationKey: string; projectKey: string }) => { href: string; newTab: boolean } | undefined;
};

export function ProjectSwitcherPopover({
  organizations,
  currentProjectKey,
  selfPlatform,
  buildHref,
  trigger,
  onOpenChange,
  onProjectSwitch,
  resolveProjectHref,
}: ProjectSwitcherPopoverProps): ReactNode {
  const [searchText, setSearchText] = useState('');

  const lowerSearchText = searchText.toLowerCase();
  const filteredOrganizations = organizations
    .map((org) => {
      const isOrgNameMatch = org.name.toLowerCase().includes(lowerSearchText) || lowerSearchText === '';
      const filteredProjects = org.projects?.filter((p) => p.name.toLowerCase().includes(lowerSearchText) || lowerSearchText === '') || [];
      if (isOrgNameMatch || filteredProjects.length > 0) {
        return { ...org, projects: isOrgNameMatch ? org.projects : filteredProjects };
      }
      return null;
    })
    .filter((org): org is GlobalNaviOrganizationV2 => org !== null);

  const isCurrentProject = (projectKey: string) => currentProjectKey !== undefined && currentProjectKey === projectKey;

  const orgSettingsHref = (organizationId: string) =>
    resolveAuthHref(fillAuthRouteTemplate(AUTH_ROUTE_TEMPLATES.organizationMembers, { organizationId }), buildHref);

  const handleProjectClick = (params: {
    organizationKey: string;
    projectKey: string;
    qantProjectId?: string;
    qantOrganizationId?: string;
    karteProjectId?: string;
  }) => {
    if (isCurrentProject(params.projectKey)) return;

    // QANT id がある行は host / default の onProjectSwitch に委譲（QANT ホストの switchProject 用）。
    if (params.qantProjectId && params.qantOrganizationId) {
      if (selfPlatform === 'karte' && !params.karteProjectId) {
        console.warn('[GlobalNavi] KARTE mapping missing for QANT project; opening on QANT', {
          projectKey: params.projectKey,
        });
      }
      onProjectSwitch({ projectId: params.qantProjectId, organizationId: params.qantOrganizationId });
      onOpenChange?.(false);
      return;
    }

    // 自基盤に無い行（例: QANT ホストの KARTE-only）は href 解決して別タブ等で開く。
    if (selfPlatform === 'qant' && !params.qantProjectId) {
      console.warn('[GlobalNavi] QANT project missing for KARTE-only row; opening on KARTE', {
        projectKey: params.projectKey,
      });
    }
    const target = resolveProjectHref?.({
      organizationKey: params.organizationKey,
      projectKey: params.projectKey,
    });
    if (target) {
      if (target.newTab) {
        window.open(target.href, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = target.href;
      }
    }
    onOpenChange?.(false);
  };

  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent side="right" align="end" sideOffset={18} className="shadow-8 p-0">
        <section className="flex flex-col w-[400px] max-h-[calc(100vh-16px)]">
          <header className="pt-6 pb-4 px-6 space-y-4">
            <h2 className="text-xl font-bold">プロジェクト設定</h2>
            <TextField
              onInputChange={(e) => setSearchText(e.target.value)}
              variant="contained"
              prepend={
                <span className="text-muted-light">
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </span>
              }
              placeholder="組織名、プロジェクト名で検索"
            />
          </header>
          <div className="grow max-h-[450px] px-6 pb-4 overflow-y-auto">
            {filteredOrganizations.length ? (
              <dl>
                {filteredOrganizations.map((organization) => {
                  const qantOrgId = organization.qantOrganizationId;
                  const orgHref = qantOrgId ? orgSettingsHref(qantOrgId) : undefined;
                  return (
                    <div key={organization.key} className="border-t border-muted first:border-t-0">
                      <dt className="flex items-center gap-3 w-full py-2.5 pl-2 border-b border-muted">
                        <span className="shrink-0 flex items-center justify-center w-8 h-8 border border-muted rounded-md overflow-hidden">
                          {organization.avatarUrl ? (
                            <img src={organization.avatarUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <FontAwesomeIcon className="text-base" icon={faBuildings} />
                          )}
                        </span>
                        <span className="grow text-sm font-bold truncate">{organization.name}</span>
                        {organization.hasOrgAdminAccess && selfPlatform !== 'karte' && orgHref && (
                          <Tooltip content="組織設定">
                            <IconButton href={orgHref.href} target="_blank" rel="noreferrer" as="a" variant="quiet" size="sm">
                              <FontAwesomeIcon icon={faCog} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </dt>
                      <div>
                        {organization.projects.map((project) => {
                          const settings = resolveProjectSettingsHref({
                            buildHref,
                            selfPlatform,
                            qantOrganizationId: qantOrgId,
                            qantProjectId: project.qantProjectId,
                            karteOrganizationId: organization.karteOrganizationId,
                            karteProjectId: project.karteProjectId,
                            hasProjectSettingsAccess: project.hasProjectSettingsAccess,
                          });
                          const showProjectSettingsGear = settings != null;
                          const missingKarteMapping = selfPlatform === 'karte' && !project.karteProjectId;
                          // 自基盤 ID を優先し、QANT ホストの KARTE-only 行は karteProjectId を出す。
                          const displayId =
                            selfPlatform === 'karte' ? project.karteProjectId : (project.qantProjectId ?? project.karteProjectId);
                          const projectHref = resolveProjectHref?.({
                            organizationKey: organization.key,
                            projectKey: project.key,
                          });
                          const rowClassName = sourTwMerge(
                            'flex items-center gap-3 w-full py-2.5 pl-2 hover:bg-action-subtle-hover active:bg-action-subtle-active transition-colors',
                            showProjectSettingsGear ? 'pr-10' : 'pr-2',
                          );
                          const rowInner = (
                            <>
                              <span className="shrink-0 flex items-center justify-center w-8 h-8 bg-action-default rounded-md overflow-hidden">
                                <FontAwesomeIcon className="text-base" icon={faBrowser} />
                              </span>
                              <div className="grow min-w-0 space-y-1 text-left">
                                <span
                                  className={sourTwMerge(
                                    'block text-sm leading-none truncate',
                                    isCurrentProject(project.key) && 'font-bold text-primary',
                                  )}
                                >
                                  {project.name}
                                </span>
                                <span className="block text-3xs text-muted leading-none truncate">
                                  {missingKarteMapping ? 'QANT で開きます' : displayId ? `ID : ${displayId}` : project.key}
                                </span>
                              </div>
                            </>
                          );
                          const onRowActivate = () =>
                            handleProjectClick({
                              organizationKey: organization.key,
                              projectKey: project.key,
                              qantProjectId: project.qantProjectId,
                              qantOrganizationId: organization.qantOrganizationId,
                              karteProjectId: project.karteProjectId,
                            });
                          return (
                            <dd key={project.key} className="relative ml-11 border-t border-muted first:border-t-0">
                              {isCurrentProject(project.key) && (
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  className="absolute top-1/2 -translate-y-1/2 -left-7 text-primary text-base"
                                />
                              )}
                              {projectHref ? (
                                <a
                                  href={projectHref.href}
                                  target={projectHref.newTab ? '_blank' : undefined}
                                  rel={projectHref.newTab ? 'noreferrer' : undefined}
                                  onClick={(event) => {
                                    // ⌘/Ctrl/Shift/Alt・中クリックはブラウザ標準（別タブ等）に委ねる
                                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                                    event.preventDefault();
                                    onRowActivate();
                                  }}
                                  className={rowClassName}
                                >
                                  {rowInner}
                                </a>
                              ) : (
                                <button type="button" onClick={onRowActivate} className={rowClassName}>
                                  {rowInner}
                                </button>
                              )}
                              {showProjectSettingsGear && (
                                <Tooltip content="プロジェクト設定">
                                  <IconButton
                                    href={settings.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    as="a"
                                    variant="quiet"
                                    size="sm"
                                    className="absolute top-1/2 -translate-y-1/2 right-0"
                                  >
                                    <FontAwesomeIcon icon={faCog} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </dd>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </dl>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 h-full py-12 text-muted-lighter">
                <div className="text-4xl">
                  <FontAwesomeIcon icon={faCrystalBall} />
                </div>
                <p className="text-sm font-medium">一致するプロジェクトはありません</p>
              </div>
            )}
          </div>
        </section>
      </PopoverContent>
    </Popover>
  );
}

export type ProjectSwitcherPopoverHelpers = {
  setIsMenuOpened: Dispatch<SetStateAction<boolean>>;
};
